import {useEffect, useState, useRef} from 'react';
import moment from 'moment';
import usePageTitle from '../../../hooks/usePageTitle';
import {useDispatch, useSelector} from "react-redux";
import {CheckIcon, ChevronDownIcon, ClipboardDocumentIcon, DocumentDuplicateIcon, ExclamationTriangleIcon, FunnelIcon, XMarkIcon} from "@heroicons/react/24/outline";
import TableSkeleton from '../../../components/TableSkeleton';
import { IconState } from '../../../components/IconState';
import {
  fetchUsers,
  selectUsersList,
  selectUserLoading,
  selectUserError,
} from "../../../slices/userSlice";
import { selectBlurSensitive } from "../../../slices/preferencesSlice";
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import { mergeSearch } from '../../../utils/urlParams';
import Pagination from '../../../components/Pagination';
import FormInput from '../../../components/FormInput';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbContext';
import { SLIDE_OVER_TOP_OFFSET_STYLE } from '../../../components/slideOverTransition';
import { LEGAL_FILTERS } from '../../../utils/legalStatus';
import { matchesUserFilters, roleValue } from '../../../utils/userFilters';
import UserFilterBar from './components/UserFilterBar';

// Separator options for the copy-emails split button. Value is what joins the
// emails; label is what the menu shows. Newline is the default (pastes cleanly
// into spreadsheets and most tools).
const COPY_SEPARATORS = {
  newline: { label: 'Newline', value: '\n' },
  comma: { label: 'Comma', value: ', ' },
  semicolon: { label: 'Semicolon', value: '; ' },
};

const loadCopySeparator = () => {
  try {
    const s = localStorage.getItem('usersCopySeparator');
    return s && COPY_SEPARATORS[s] ? s : 'newline';
  } catch {
    return 'newline';
  }
};

export default function Users() {
  usePageTitle('Users');
  useBreadcrumbs([{ label: 'Users' }, { label: 'List Users' }], 'Browse and manage registered platform users');
  const dispatch = useDispatch();
  const usersList = useSelector(selectUsersList);
  const loading = useSelector(selectUserLoading);
  const errorMessage = useSelector(selectUserError);
  const blurSensitive = useSelector(selectBlurSensitive);
  const navigate = useNavigate();
  const location = useLocation();
  const isRestoringFromURL = useRef(false);

  // Local states for filtering & selected user (and pagination)
  const [filteredUsers, setFilteredUsers] = useState([]);
  // initialize search, pagination, filters from query params if present so we can return to the same state
  const qs = new URLSearchParams(location.search);
  const initialSearch = qs.get('search') || '';
  const initialPage = (() => { const p = Number(qs.get('page')); return Number.isFinite(p) && p > 0 ? p : 1; })();
  const initialPageSize = (() => { const s = Number(qs.get('pageSize')); return Number.isFinite(s) && s > 0 ? s : 9; })();
  const initialSortBy = qs.get('sortBy') || 'name';
  const initialRoleFilter = qs.get('roleFilter') || 'all';
  const initialActivityFilter = qs.get('activityFilter') || 'all';
  const initialLegalFilter = qs.get('legalFilter') || 'all';
  const [searchEmail, setSearchEmail] = useState(initialSearch);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [sortDir] = useState('asc'); // 'asc' or 'desc' (read-only for now)
  const [roleFilter, setRoleFilter] = useState(initialRoleFilter);
  const [activityFilter, setActivityFilter] = useState(initialActivityFilter);
  const [legalFilter, setLegalFilter] = useState(initialLegalFilter);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [copySeparator, setCopySeparator] = useState(loadCopySeparator);
  const [isCopyMenuOpen, setIsCopyMenuOpen] = useState(false);
  const copyMenuRef = useRef(null);

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  // Persist the chosen separator so the common case stays one click next time.
  useEffect(() => {
    try {
      localStorage.setItem('usersCopySeparator', copySeparator);
    } catch {
      // ignore storage failures (private mode, quota); pref stays in-memory only
    }
  }, [copySeparator]);

  // Close the separator menu on outside click.
  useEffect(() => {
    if (!isCopyMenuOpen) return undefined;
    const onPointerDown = (e) => {
      if (copyMenuRef.current && !copyMenuRef.current.contains(e.target)) {
        setIsCopyMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isCopyMenuOpen]);

  // Watch for URL changes (e.g., when navigating back from user detail) and update state
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlPage = params.get('page');
    const urlPageSize = params.get('pageSize');
    const urlSearch = params.get('search');
    const urlSortBy = params.get('sortBy');
    const urlRoleFilter = params.get('roleFilter');
    const urlActivityFilter = params.get('activityFilter');
    const urlLegalFilter = params.get('legalFilter');

    // Mark that we're restoring from URL to prevent other effects from interfering
    isRestoringFromURL.current = true;

    // Update search (equality guard: avoid re-renders when value unchanged)
    const newSearch = urlSearch || '';
    setSearchEmail((prev) => prev !== newSearch ? newSearch : prev);

    // Update sortBy
    if (urlSortBy) {
      setSortBy((prev) => prev !== urlSortBy ? urlSortBy : prev);
    }

    // Update roleFilter
    if (urlRoleFilter) {
      setRoleFilter((prev) => prev !== urlRoleFilter ? urlRoleFilter : prev);
    }

    // Update activityFilter
    if (urlActivityFilter) {
      setActivityFilter((prev) => prev !== urlActivityFilter ? urlActivityFilter : prev);
    }

    // Update legalFilter (Terms of Use acceptance)
    if (urlLegalFilter) {
      setLegalFilter((prev) => prev !== urlLegalFilter ? urlLegalFilter : prev);
    }

    // Update pageSize
    if (urlPageSize) {
      const size = Number(urlPageSize);
      if (Number.isFinite(size) && size > 0) {
        setPageSize((prev) => prev !== size ? size : prev);
      }
    }

    // Update page last (after filters are set)
    if (urlPage) {
      const page = Number(urlPage);
      if (Number.isFinite(page) && page > 0) {
        setCurrentPage((prev) => prev !== page ? page : prev);
      }
    } else {
      setCurrentPage((prev) => prev !== 1 ? 1 : prev);
    }

    // Clear the flag after a short delay to allow all effects to complete
    setTimeout(() => {
      isRestoringFromURL.current = false;
    }, 100);
  }, [location.search]); // Only depend on location.search to avoid infinite loops

  // State -> URL (replace) so the list view is shareable and refresh-safe.
  useEffect(() => {
    if (isRestoringFromURL.current) return;
    const next = mergeSearch(location.search, {
      page: currentPage > 1 ? currentPage : null,
      pageSize: pageSize !== 9 ? pageSize : null,
      search: searchEmail.trim() || null,
      sortBy: sortBy !== 'name' ? sortBy : null,
      roleFilter: roleFilter !== 'all' ? roleFilter : null,
      activityFilter: activityFilter !== 'all' ? activityFilter : null,
      legalFilter: legalFilter !== 'all' ? legalFilter : null,
    });
    if (next !== location.search) {
      navigate({ search: next }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, searchEmail, sortBy, roleFilter, activityFilter, legalFilter]);

  const currentFilters = { search: searchEmail, role: roleFilter, activity: activityFilter, legal: legalFilter };
  // Users a filter option would leave, given the other filters (chip menus).
  const countFor = (key, value) => (usersList || []).filter((u) => matchesUserFilters(u, { ...currentFilters, [key]: value })).length;
  const filtersActive = roleFilter !== 'all' || activityFilter !== 'all' || legalFilter !== 'all';
  const setFilter = (key, value) => {
    ({ role: setRoleFilter, activity: setActivityFilter, legal: setLegalFilter })[key](value);
    setCurrentPage(1);
  };
  const clearFilters = () => { setRoleFilter('all'); setActivityFilter('all'); setLegalFilter('all'); setCurrentPage(1); };

  // Update filteredUsers whenever usersList or searchEmail changes
  useEffect(() => {
    if (!usersList || usersList.length === 0) {
      setFilteredUsers([]);
      return;
    }
    // Search, role, activity and Terms of Use (see utils/userFilters.js; the
    // chip menus count with the same predicate).
    let newFilteredUsers = usersList.filter((u) => matchesUserFilters(u, currentFilters));

    // Sorting
    const getName = (u) => ((u.first_name || '') + (u.last_name ? ' ' + u.last_name : '')).trim() || u.username || u.email || '';
    const getValue = (u) => {
      switch (sortBy) {
        case 'name': return (getName(u) || '').toLowerCase();
        case 'email': return (u.email || '').toLowerCase();
        case 'date_joined': return u.date_joined ? Date.parse(u.date_joined) || 0 : 0;
        case 'last_login': return u.last_login ? Date.parse(u.last_login) || 0 : 0;
        case 'is_verified': return u.is_verified ? 1 : 0;
        case 'role': return roleValue(u);
        default: return (getName(u) || '').toLowerCase();
      }
    };

    newFilteredUsers.sort((a, b) => {
      const va = getValue(a);
      const vb = getValue(b);
      const dir = sortDir === 'asc' ? 1 : -1;
      // numeric comparison
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      // fallback to string comparison
      return String(va).localeCompare(String(vb)) * dir;
    });

    setFilteredUsers(newFilteredUsers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersList, searchEmail, sortBy, sortDir, pageSize, roleFilter, activityFilter, legalFilter]);

  // Pagination helpers
  const totalItems = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  useEffect(() => {
    // Only clamp the page if we're not restoring from URL
    if (!isRestoringFromURL.current && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedUsers = filteredUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const emailCount = filteredUsers.filter(u => u.email).length;
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  // Handle row/card click to navigate to the user's profile page
  const openEditor = (user) => {
    if (!user) return;
    const params = new URLSearchParams();
    // Add source indicator
    params.set('source', 'list');
    // Preserve current Users list filters
    if (currentPage) params.set('page', String(currentPage));
    if (pageSize) params.set('pageSize', String(pageSize));
    if (searchEmail.trim()) params.set('search', searchEmail.trim());
    if (sortBy && sortBy !== 'name') params.set('sortBy', sortBy);
    if (roleFilter && roleFilter !== 'all') params.set('roleFilter', roleFilter);
    if (activityFilter && activityFilter !== 'all') params.set('activityFilter', activityFilter);
    if (legalFilter && legalFilter !== 'all') params.set('legalFilter', legalFilter);
    navigate(`/users/${user.id}?${params.toString()}`);
  };

  // Copy the currently filtered/searched emails, joined by the chosen separator.
  const copyAllEmails = async () => {
    const emails = filteredUsers.map(u => u.email).filter(Boolean);
    if (emails.length === 0) {
      toast.info('No emails to copy');
      return;
    }
    const joined = emails.join(COPY_SEPARATORS[copySeparator].value);
    try {
      await navigator.clipboard.writeText(joined);
      toast.success(`Copied ${emails.length} email${emails.length !== 1 ? 's' : ''}`);
    } catch (err) {
      console.error('Copy failed', err);
      toast.error('Copy failed');
    }
  };


  // Loading / Error states
  if (loading) {
    return <TableSkeleton rows={8} className="mt-8" />;
  }

  if (errorMessage) {
    return (
      <div className="mt-8">
        <IconState
          icon={ExclamationTriangleIcon}
          title="Something went wrong"
          description={errorMessage}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="mt-8">
        {/* Mobile: Search + Filter button + Copy */}
        <div className="sm:hidden flex gap-2 mb-4">
          <div className="flex-1">
            <FormInput
              type="text"
              leftIcon="user"
              size="medium"
              placeholder="Search..."
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
            />
          </div>
          <button
            onClick={() => setIsFilterPanelOpen(true)}
            className="flex items-center justify-center px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            title="Filter"
          >
            <FunnelIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={copyAllEmails}
            title="Copy all emails"
            className="flex items-center justify-center p-2 rounded-md bg-brand-50 hover:bg-brand-100 dark:bg-brand-900 dark:hover:bg-brand-800 text-brand-700 dark:text-brand-100 border border-transparent hover:shadow-sm transition"
          >
            <DocumentDuplicateIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Desktop: search, filter chips (Role, Activity, Terms, Sort), copy */}
        <div className="hidden sm:flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-[16rem]">
            <FormInput
              type="text"
              leftIcon="user"
              size="small"
              className="h-8 text-[13px]"
              placeholder="Search by email or name"
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
            />
          </div>
          <UserFilterBar
            filters={{ role: roleFilter, activity: activityFilter, legal: legalFilter }}
            onChange={setFilter}
            sortBy={sortBy}
            onSortChange={(v) => { setSortBy(v); setCurrentPage(1); }}
            countFor={countFor}
          />
          {filtersActive && (
            <button type="button" onClick={clearFilters} className="px-1 text-xs font-medium text-brand-600 hover:underline dark:text-brand-400">
              Clear filters
            </button>
          )}
          <div className="relative inline-flex" ref={copyMenuRef}>
            <button
              type="button"
              aria-label={`Copy ${emailCount} email${emailCount !== 1 ? 's' : ''}`}
              onClick={copyAllEmails}
              title="Copy emails (current filter)"
              className="inline-flex items-center gap-1.5 text-xs font-medium pl-2.5 pr-2 rounded-l-md bg-brand-50 hover:bg-brand-100 dark:bg-brand-900 dark:hover:bg-brand-800 text-brand-700 dark:text-brand-100 border border-transparent hover:shadow-sm transition h-8"
            >
              <DocumentDuplicateIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Copy</span>
              <span className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 text-xs bg-white text-brand-700 rounded-full dark:bg-brand-600 dark:text-white dark:ring-1 dark:ring-white/10">{emailCount}</span>
            </button>
            <button
              type="button"
              aria-label="Copy separator options"
              aria-haspopup="menu"
              aria-expanded={isCopyMenuOpen}
              onClick={() => setIsCopyMenuOpen((o) => !o)}
              className="inline-flex items-center justify-center px-1.5 rounded-r-md bg-brand-50 hover:bg-brand-100 dark:bg-brand-900 dark:hover:bg-brand-800 text-brand-700 dark:text-brand-100 border border-transparent border-l border-l-brand-200 dark:border-l-brand-700 hover:shadow-sm transition h-8"
            >
              <ChevronDownIcon className="w-4 h-4" />
            </button>
            {isCopyMenuOpen && (
              <div role="menu" className="absolute right-0 top-full mt-1 z-20 w-40 rounded-md border border-gray-200 dark:border-gray-600 bg-surface dark:bg-gray-870 shadow-lg py-1">
                <div className="px-3 py-1 text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">Separator</div>
                {Object.entries(COPY_SEPARATORS).map(([key, s]) => (
                  <button
                    key={key}
                    type="button"
                    role="menuitemradio"
                    aria-checked={copySeparator === key}
                    onClick={() => { setCopySeparator(key); setIsCopyMenuOpen(false); }}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <span>{s.label}</span>
                    {copySeparator === key && <CheckIcon className="w-4 h-4 text-brand-600 dark:text-brand-300" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Filter Side Panel (Mobile) */}
        {isFilterPanelOpen && (
          <>
            <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setIsFilterPanelOpen(false)} />
            <div className="fixed inset-y-0 right-0 w-full sm:w-96 bg-white dark:bg-gray-870 shadow-xl z-50 overflow-y-auto" style={SLIDE_OVER_TOP_OFFSET_STYLE}>
              <div className="sticky top-0 bg-surface dark:bg-gray-870 border-b border-gray-200/70 dark:border-gray-600/70 px-6 py-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Filter Users</h2>
                <button
                  onClick={() => setIsFilterPanelOpen(false)}
                  className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="px-6 py-4 space-y-6">
                {/* Sort By */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Sort By
                  </label>
                  <div className="space-y-2">
                    {['name', 'email', 'date_joined', 'last_login', 'is_verified'].map(option => (
                      <div
                        key={option}
                        onClick={() => {
                          setSortBy(option);
                          setCurrentPage(1);
                        }}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          sortBy === option
                            ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30'
                            : 'border-gray-200/70 dark:border-gray-600/70 hover:border-brand-300 dark:hover:border-brand-700'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {option === 'name' && 'Name'}
                            {option === 'email' && 'Email'}
                            {option === 'date_joined' && 'Joined Date'}
                            {option === 'last_login' && 'Last Seen'}
                            {option === 'is_verified' && 'Verified'}
                          </div>
                          {sortBy === option && (
                            <div className="h-5 w-5 rounded-full bg-brand-600 flex items-center justify-center">
                              <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Role Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Filter by Role
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: 'all', label: 'All Roles' },
                      { value: 'forecaster', label: 'Forecaster' },
                      { value: 'market_owner', label: 'Market Owner' },
                      { value: 'admin', label: 'Administrator' }
                    ].map(option => (
                      <div
                        key={option.value}
                        onClick={() => {
                          setRoleFilter(option.value);
                          setCurrentPage(1);
                        }}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          roleFilter === option.value
                            ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30'
                            : 'border-gray-200/70 dark:border-gray-600/70 hover:border-brand-300 dark:hover:border-brand-700'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{option.label}</div>
                          {roleFilter === option.value && (
                            <div className="h-5 w-5 rounded-full bg-brand-600 flex items-center justify-center">
                              <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Terms of Use acceptance */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Filter by Terms of Use
                  </label>
                  <div className="space-y-2">
                    {LEGAL_FILTERS.map(option => (
                      <div
                        key={option.value}
                        onClick={() => {
                          setLegalFilter(option.value);
                          setCurrentPage(1);
                        }}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          legalFilter === option.value
                            ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30'
                            : 'border-gray-200/70 dark:border-gray-600/70 hover:border-brand-300 dark:hover:border-brand-700'
                        }`}
                      >
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{option.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Activity Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Filter by Activity
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: 'all', label: 'All Activity' },
                      { value: 'active_30', label: 'Active (last 30 days)' },
                      { value: 'inactive_week', label: 'Inactive (1+ week)' },
                      { value: 'never_logged_in', label: 'Never Logged In' }
                    ].map(option => (
                      <div
                        key={option.value}
                        onClick={() => {
                          setActivityFilter(option.value);
                          setCurrentPage(1);
                        }}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          activityFilter === option.value
                            ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30'
                            : 'border-gray-200/70 dark:border-gray-600/70 hover:border-brand-300 dark:hover:border-brand-700'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{option.label}</div>
                          {activityFilter === option.value && (
                            <div className="h-5 w-5 rounded-full bg-brand-600 flex items-center justify-center">
                              <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Cards area with fixed height for consistent pagination position */}
        <div style={{ minHeight: `${Math.ceil(pageSize / 3) * 220 + 120}px` }}>
          {/* Cards list */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedUsers.length > 0 ? paginatedUsers.map(u => (
              <article key={u.id}
                        tabIndex={0}
                        role="button"
                        onClick={() => openEditor(u)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEditor(u); } }}
                        className="relative overflow-visible rounded-xl p-6 bg-transparent border border-gray-200/70 dark:border-gray-600/70 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-200 dark:focus:ring-brand-300 cursor-pointer">

                 {/* top row: content (no avatar) */}
                 <div className="flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 pr-6 flex-1">
                        <div className={`text-sm font-semibold text-gray-900 dark:text-gray-100 truncate ${blurSensitive ? 'blur-sm select-none' : ''}`}>{(u.first_name || '') + (u.last_name ? ' ' + u.last_name : '') || u.username || u.email}</div>
                        {/* show email with truncation */}
                        <div className={`text-xs text-gray-500 dark:text-gray-300 mt-1 truncate ${blurSensitive ? 'blur-sm select-none' : ''}`} title={u.email}>{u.email}</div>
                        {u.username && <div className="text-xs text-gray-500 dark:text-gray-300 mt-1">@{u.username}</div>}

                        {/* Role display */}
                        <div className="mt-2">
                          <span className="inline-flex items-center text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-2 py-1 rounded font-medium">
                            {u.is_superuser ? 'Administrator' : (u.is_forecaster ? 'Forecaster' : 'Market Owner')}
                          </span>
                          {/* Terms of Use: only forecasters are asked (null for everyone else) */}
                          {u.legal_accepted === true && (
                            <span className="ml-2 inline-flex items-center text-xs bg-green-50 dark:bg-green-900 text-green-800 dark:text-green-300 px-2 py-1 rounded">Terms accepted</span>
                          )}
                          {u.legal_accepted === false && (
                            <span className="ml-2 inline-flex items-center text-xs bg-amber-50 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 px-2 py-1 rounded">Terms pending</span>
                          )}
                        </div>

                        {/* Last seen with warning for never logged in */}
                        <div className="mt-2">
                          {u.last_login ? (
                            <span className="text-xs text-gray-500 dark:text-gray-400">Last seen {moment(u.last_login).fromNow()}</span>
                          ) : (
                            <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Never logged in</span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 ml-2">
                        {u.is_verified && <span className="text-xs bg-green-50 dark:bg-green-900 text-green-800 dark:text-green-300 px-2 py-0.5 rounded">Verified</span>}
                        {u.is_superuser && <span className="text-xs bg-brand-50 dark:bg-brand-900 text-brand-800 dark:text-brand-200 px-2 py-0.5 rounded">Admin</span>}
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-between">
                      <div className="text-xs text-gray-500 dark:text-gray-400">Joined {u.date_joined ? moment(u.date_joined).format('ll') : '—'}</div>

                      <div className="flex items-center gap-2">
                        {/* icon-only copy; stop propagation so click doesn't open panel */}
                        <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(u.email || ''); toast.info('Copied email'); }} aria-label="Copy email" className="p-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600">
                          <ClipboardDocumentIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            )) : (
              <div className="text-sm text-gray-500 p-4 col-span-full">No users found.</div>
            )}
          </div>

          {/* Pagination controls - positioned at bottom of fixed-height container */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={pageSize}
            onPageChange={handlePageChange}
            onItemsPerPageChange={(newSize) => {
              setPageSize(newSize);
              setCurrentPage(1);
            }}
            itemsPerPageOptions={[6, 9, 12, 18, 30]}
          />
        </div>

      </div>
    </div>
  );
}
